import { useEffect, useRef, useCallback, RefObject } from 'react';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { resizeAndRefitViewports } from '../utils/engineResize';

interface UseViewportResizeOptions {
  /** The container element to observe for size changes */
  containerRef: RefObject<HTMLDivElement | null>;
  /** The Cornerstone rendering engine */
  renderingEngine: CornerstoneTypes.IRenderingEngine | null;
  /** Optional: specific viewport ID to refit (for single viewport mode) */
  viewportId?: string;
}

/**
 * Hook that handles viewport resizing when the container element changes size.
 * Uses ResizeObserver to detect container size changes and throttles resize calls
 * to one per animation frame for smooth, responsive resizing.
 *
 * After renderingEngine.resize(), each viewport gets:
 * - a refit to the new container dimensions that re-applies the user's pan/zoom
 * - For video viewports: customRenderViewportToCanvas() to force a synchronous
 *   redraw (Cornerstone skips renderFrame() when playing, causing a black flash)
 *
 * Force-clear before resize: ContextPoolRenderingEngine._resizeVTKViewports
 * bails out (`if (this._animationFrameSet) return`) without resizing the stack
 * canvas buffers whenever an engine render is already scheduled. A playing
 * VideoViewport fires IMAGE_RENDERED every frame, keeping a render perpetually
 * pending — so stack viewports sharing the engine never resize and their image
 * gets bitmap-stretched (broken aspect ratio). We clear the pending frame first
 * so the resize always applies; resize(immediate=true) reschedules the render.
 */
export function useViewportResize({
  containerRef,
  renderingEngine,
  viewportId,
}: UseViewportResizeOptions): void {
  const rafRef = useRef<number | null>(null);
  const isResizingRef = useRef(false);

  const runResize = useCallback(() => {
    if (!renderingEngine || isResizingRef.current) return;
    isResizingRef.current = true;
    try {
      resizeAndRefitViewports(renderingEngine, { viewportId });
    } catch {
      // Resize error - viewport may be in transition
    } finally {
      isResizingRef.current = false;
    }
  }, [renderingEngine, viewportId]);

  const handleResize = useCallback(() => {
    if (!renderingEngine || isResizingRef.current) return;

    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
    }

    // Throttle to one resize per animation frame (~60fps)
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      runResize();
    });
  }, [renderingEngine, runResize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !renderingEngine) return () => {};

    const resizeObserver = new ResizeObserver((entries) => {
      const hasValidEntry = entries.some(
        (entry) => entry.contentRect.width > 0 && entry.contentRect.height > 0
      );
      if (hasValidEntry) {
        handleResize();
      }
    });

    resizeObserver.observe(container);

    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      resizeObserver.disconnect();
    };
  }, [containerRef, renderingEngine, handleResize]);
}
